/**
 * 2.1 bloque C — Cuentas duplicadas (`ai.duplicate_accounts`): cuentas que comparten dispositivo,
 * tarjeta, cuenta bancaria de cobro o teléfono verificado. Solo avisa: bloquear o no lo decide el
 * admin desde la ficha del usuario. «Revisado» / «Descartar» lo quitan de Abiertos (vuelve solo si
 * otra cuenta nueva comparte el mismo dato).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, muted, Notice, str, Table, td } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Status = 'open' | 'reviewed' | 'dismissed';

const KIND: Record<string, string> = {
  device: 'Mismo dispositivo',
  card: 'Misma tarjeta',
  iban: 'Misma cuenta bancaria (IBAN)',
  phone: 'Mismo teléfono',
};
const STATUS_LABEL: Record<Status, string> = {
  open: 'Abiertos',
  reviewed: 'Revisados',
  dismissed: 'Descartados',
};

function when(v: unknown): string {
  const d = (v as { toDate?: () => Date } | null)?.toDate?.();
  return d ? d.toLocaleString('es-ES') : '';
}

export function DuplicateAccountsPage(): ReactElement {
  const [status, setStatus] = useState<Status>('open');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    setRows(null);
    getDocs(
      query(
        collection(db, 'duplicateAccountAlerts'),
        where('status', '==', status),
        orderBy('last_seen_at', 'desc'),
        limit(200),
      ),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [status, version]);

  const review = (id: string, next: Status): void => {
    const note = next === 'open' ? '' : (window.prompt('Nota (opcional):') ?? '');
    setError(null);
    call(api.ai.reviewDuplicateAccount, {
      alert_id: id,
      status: next,
      ...(note ? { note } : {}),
    })
      .then(() => {
        setNotice('Hecho.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };

  const scan = (): void => {
    setScanning(true);
    setError(null);
    call(api.ai.scanDuplicateAccounts, {})
      .then((r) => {
        setNotice(
          `Revisados ${r.users} perfiles con teléfono verificado: ${r.shared_signals} teléfonos compartidos.`,
        );
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setScanning(false);
      });
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Cuentas duplicadas</h1>
      <p style={muted}>
        Cuentas que comparten un dato con otra (bloqueada o no). No se bloquea nada automáticamente:
        abre cada cuenta y decide. Se guardan solo huellas (hash), nunca el número de tarjeta, el
        IBAN ni el teléfono. Dispositivos, tarjetas e IBAN se apuntan desde que se enciende el
        interruptor; los teléfonos verificados ya existentes se pueden revisar con el botón.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['open', 'reviewed', 'dismissed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            style={btn(status === s)}
            onClick={() => {
              setStatus(s);
            }}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
        <button type="button" style={btn(false)} disabled={scanning} onClick={scan}>
          {scanning ? 'Revisando…' : 'Revisar teléfonos existentes'}
        </button>
      </div>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Dato compartido', 'Cuentas', 'Última vez', 'Nota', 'Acciones']}>
          {rows.map((r) => {
            const accounts = (r.accounts as Doc[] | undefined) ?? [];
            return (
              <tr key={str(r.id)}>
                <td style={td}>
                  <strong>{KIND[str(r.kind)] ?? str(r.kind)}</strong>
                  {r.any_banned === true && (
                    <div style={{ fontSize: 12, color: '#B91C1C' }}>Una cuenta está bloqueada</div>
                  )}
                </td>
                <td style={td}>
                  {accounts.map((a) => (
                    <div key={str(a.uid)}>
                      <Link to={`/usuarios/${str(a.uid)}`}>
                        @{str(a.username) || str(a.uid).slice(-6)}
                      </Link>
                      {a.banned === true ? ' (bloqueada)' : ''}
                      {str(a.uid) === str(r.last_uid) ? ' · la última' : ''}
                    </div>
                  ))}
                </td>
                <td style={td}>{when(r.last_seen_at)}</td>
                <td style={td}>{str(r.note)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {status !== 'reviewed' && (
                    <button
                      type="button"
                      style={btn(true)}
                      onClick={() => {
                        review(str(r.id), 'reviewed');
                      }}
                    >
                      Revisado
                    </button>
                  )}{' '}
                  {status !== 'dismissed' && (
                    <button
                      type="button"
                      style={btn(false)}
                      onClick={() => {
                        review(str(r.id), 'dismissed');
                      }}
                    >
                      Descartar
                    </button>
                  )}{' '}
                  {status !== 'open' && (
                    <button
                      type="button"
                      style={btn(false)}
                      onClick={() => {
                        review(str(r.id), 'open');
                      }}
                    >
                      Reabrir
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={5}>
                Nada en esta lista.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
